/**
 * LoginPage - Страница входа
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/layouts";
import { useAuthContext } from "../../contexts/AuthContext/AuthProvider";
import { authService } from "../../api/services/authService";
import { browserName, osName } from "react-device-detect";

const LoginPage = () => {
    const { login, loading } = useAuthContext();
    const navigate = useNavigate();

    const [showError, setShowError] = useState(false);
    const [gAuthNeeds, setGAuthNeeds] = useState(false);
    const [verifyAuthErr, setVerifyAuthErr] = useState("");

    const [values, setValues] = useState({ login: "", password: "" });
    const [verificationCode, setVerificationCode] = useState("");
    const [ip, setIp] = useState("unknown");
    const [isConfirming, setIsConfirming] = useState(false);

    useEffect(() => {
        const loadIp = async () => {
            try {
                const clientIp = await authService.getIp();
                setIp(clientIp);
            } catch {
                setIp("unknown");
            }
        };
        loadIp();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setShowError(false);
        setVerifyAuthErr("");

        const result = await login({
            login: values.login,
            password: values.password,
        });

        if (result.success) {
            navigate("/ucp/");
            return;
        }

        if (result.requires2FA) {
            setGAuthNeeds(true);
            localStorage.setItem("user-login", values.login);
            localStorage.setItem("user-password", values.password);
            return;
        }

        setShowError(true);
    };

    const handleVerifyAuth = async (e) => {
        e.preventDefault();

        setShowError(false);
        setVerifyAuthErr("");

        try {
            setIsConfirming(true);
            const data = await authService.confirmAuth({
                user_name: localStorage.getItem("user-login"),
                user_password: localStorage.getItem("user-password"),
                ip,
                client: browserName,
                platform: osName,
                verification_code: verificationCode,
            });

            if (data?.status === "error") {
                setShowError(true);
                setVerifyAuthErr(data.message || "Ошибка подтверждения");
                return;
            }

            // Sometimes confirm-auth returns user data without token.
            // Save what we have, then re-login to obtain JWT.
            authService.saveUserData(data);

            const tokenAfterConfirm = localStorage.getItem("jwtToken");
            if (!tokenAfterConfirm) {
                const relogin = await login({
                    login: localStorage.getItem("user-login") || values.login,
                    password:
                        localStorage.getItem("user-password") || values.password,
                });
                if (!relogin?.success) {
                    setShowError(true);
                    setVerifyAuthErr(
                        relogin?.error ||
                            data?.message ||
                            "Не удалось завершить авторизацию",
                    );
                    return;
                }
            }

            // Force UCP to mount with authenticated context
            window.location.href = "/ucp/";
        } catch (err) {
            setShowError(true);
            setVerifyAuthErr(err.message);
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <AuthLayout>
            <div className="auth-container">
                {!gAuthNeeds ? (
                    <>
                        <span className="auth-title">АВТОРИЗАЦИЯ</span>
                        <form className="auth-form" onSubmit={handleSubmit}>
                            {showError && (
                                <span className="auth-error-span">
                                    Неверное имя пользователя или пароль!
                                </span>
                            )}
                            <input
                                className="auth-input-form"
                                placeholder="Имя аккаунта или E-mail"
                                name="login"
                                value={values.login}
                                onChange={handleChange}
                            />
                            <input
                                className="auth-input-form"
                                placeholder="Пароль"
                                name="password"
                                type="password"
                                value={values.password}
                                onChange={handleChange}
                            />
                            <input type="hidden" name="ip" value={ip} />
                            <input
                                type="hidden"
                                name="client"
                                value={browserName}
                            />
                            <input
                                type="hidden"
                                name="platform"
                                value={osName}
                            />

                            <button
                                className="auth-input-button-login"
                                type="submit"
                                disabled={loading}
                                aria-busy={loading}
                            >
                                <span
                                    className="auth-input-button-login__label"
                                    style={{ opacity: loading ? 0 : 1 }}
                                >
                                    ВОЙТИ
                                </span>
                                {loading ? (
                                    <span className="btn__loader" aria-label="Загрузка">
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                    </span>
                                ) : null}
                            </button>
                        </form>
                        <div className="auth-input-info-container">
                            <a href="/recovery">
                                Восстановить доступ
                                <br />
                            </a>
                            <a
                                href="/register"
                                className="auth-input-no-account-title"
                            >
                                Нет аккаунта? Зарегистрироваться
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="auth-title">Подтверждение кода</span>
                        <span
                            style={{
                                left: "0",
                                marginLeft: "0",
                                display: "block",
                                position: "relative",
                                textAlign: "left",
                                marginTop: "20px",
                            }}
                        >
                            На вашем аккаунте включена двухфакторная
                            аутентификация. Пожалуйста, зайдите в приложение
                            Google Authenticator и введите код для продолжения
                            аутентификации.
                        </span>
                        <form className="auth-form" onSubmit={handleVerifyAuth}>
                            {showError && (
                                <span className="auth-error-span">
                                    {verifyAuthErr}
                                </span>
                            )}
                            <input
                                className="auth-input-form"
                                placeholder="Код аутентификации"
                                name="verification_code"
                                required
                                minLength={6}
                                maxLength={6}
                                value={verificationCode}
                                onChange={(e) =>
                                    setVerificationCode(e.target.value)
                                }
                            />
                            <input type="hidden" name="ip" value={ip} />
                            <input
                                type="hidden"
                                name="client"
                                value={browserName}
                            />
                            <input
                                type="hidden"
                                name="platform"
                                value={osName}
                            />

                            <button
                                className="auth-input-button-login"
                                type="submit"
                                style={{ marginBottom: "40px" }}
                                disabled={loading || isConfirming}
                                aria-busy={loading || isConfirming}
                            >
                                <span
                                    className="auth-input-button-login__label"
                                    style={{
                                        opacity: loading || isConfirming ? 0 : 1,
                                    }}
                                >
                                    ВОЙТИ
                                </span>
                                {loading || isConfirming ? (
                                    <span className="btn__loader" aria-label="Загрузка">
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                    </span>
                                ) : null}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </AuthLayout>
    );
};

export default LoginPage;
